import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../hooks/hooks";
import { fetchAxiosNoToken } from "../../../helpers/fetchAxiosNoToken";
import { set_landing_house } from "../../../redux/reducers/landingReducer";
import { formatearNumero } from "../../../components/helpers/formatearNumero";
import { NavbarsAvatar } from "../../../components/NavbarsAvatar";
import { ModalLogin } from "../../ModalLogin";
import { MercadoPago } from "../../mercadoPago/MercadoPago";
import NiubizPayButton from "./NiubizPayButton";
import {
    DOC_EXTRANJERO_SIN_IGV,
    DOC_OPCIONES_FACTURA,
    FacturacionForm,
    TipoDocSunat,
} from "./facturacion_form.types";
import { ModalDatosComprobante } from "./ModalDatosComprobante";

/* ---------------------- tipos y constantes ---------------------- */

/* ------------------------- componente página ------------------------- */

interface Pedido {
    id: number;
    id_cliente: number;
    id_habitacion: number;
    costo_alquiler: string;
    garantia: string;
    tipo_garantia: string;
    descuento_estadia: string;
    descuento_cupon: string;
    costo_servicios_adicionales: string;
    costo_total: string;
    pagado: string;
    saldo: string;
    habilitado: string;
    estado: string;
    fecha_inicio: string;
    fecha_final: string;
    tipo_moneda: string;
    fecha_registro: string;
    adultos: number;
    ninos: number;
    oferta: string;
    expiracion: any;
    pedidos_servicios_adicionales: {
        id: number;
        servicio: string;
        costo: string;
    }[];
    HotelHabitaciones: {
        id: number;
        nombre: string;
        banner: string;
        casa: any;
    };

    pedidos_tours: Tour[];
}

export interface Tour {
    id: number;
    id_tour: number;
    id_pedido_habitaciones: number;
    hora_inicio: string;
    hora_final: string;
    precio_simple: string;
    numero_adultos: number;
    numero_ninos: number;
    precio_tour: string;
    fecha: string;
    hotel_tour: HotelTour;
}
export interface HotelTour {
    id: number;
    nombre: string;
    banner: string;
    casa: Casa;
}
export interface Casa {
    id: number;
    nombre: string;
    banner: string;
    Propietario: Propietario;
}
export interface Propietario {
    id: number;
    nombre: string;
    apellido: string;
    avatar: string;
}
export const CheckoutPagoHotel = () => {
    const dispatch = useDispatch();
    const searchParams = new URLSearchParams(window.location.search);
    const paymentFailed = searchParams.get("paymentFailed");
    const [niubizRenderKey, setNiubizRenderKey] = useState<number>(Date.now());
    const { id, tipo_usuario } = useAppSelector((s) => s.usuario_logeado);
    const { house } = useAppSelector((s) => s.landing_house);
    const [showNiubiz, setShowNiubiz] = useState(false);
    const { id_reserva } = useParams();
    const navigate = useNavigate();

    const [formaPago, setFormaPago] = useState("Transferencia bancaria");
    const [modalVisibleLogin, setModalVisibleLogin] = useState(false);
    const [pagoUnico, setPagoUnico] = useState(true);
    const [comprobantePreview, setComprobantePreview] = useState<string | null>(
        null,
    );
    const [comprobante, setComprobante] = useState<File | null>(null);
    const [pedido, setPedido] = useState<Pedido | null>(null);
    const [enviando, setEnviando] = useState(false);

    // comprobante (Factura/Boleta) – sólo primer pago
    const [isComprobante, setIsComprobante] = useState(false);
    const [showModalComprobante, setShowModalComprobante] = useState(false);
    const [factData, setFactData] = useState<FacturacionForm | null>(null);

    // MP
    const [llavePublica, setLlavePublica] = useState<null | string>(null);
    const [mercadoPago, setMercadoPago] = useState<null | string>(null);

    // % primer pago (dos pagos)
    const [montoAPagar, setMontoAPagar] = useState<number>(
        Number(house.porcentaje_primer_pago),
    );

    const regenerateNiubizButton = () => {
        setNiubizRenderKey(Date.now());
    };

    useEffect(
        () => setMontoAPagar(Number(house.porcentaje_primer_pago)),
        [house],
    );

    const baseTotal = useMemo(
        () => (pedido ? Number(pedido.costo_total) : 0),
        [pedido],
    );

    const isIgv = useMemo(() => {
        if (!isComprobante || !factData) return false;
        return !DOC_EXTRANJERO_SIN_IGV.includes(
            factData.tipo_documento_identidad,
        );
    }, [isComprobante, factData]);

    const igv = useMemo(
        () =>
            isIgv && pedido
                ? Number(
                      ((baseTotal - Number(pedido?.garantia)) * 0.18).toFixed(
                          2,
                      ),
                  )
                : 0,
        [isIgv, baseTotal],
    );
    const totalConIgv = useMemo(
        () => Number((baseTotal + igv).toFixed(2)),
        [baseTotal, igv],
    );

    const mostrarOpcionComprobante = pedido && pedido.estado !== "Pago Parcial";

    // load
    const getHouseFromTenant = async () => {
        const data = await fetchAxiosNoToken({ url: `/casa/getOne/dominio` });
        dispatch(set_landing_house(data));
        setLlavePublica(data.llave_publica_mp || null);
        setMercadoPago(data.is_mercado_pago || null);
    };
    useEffect(() => {
        // setModalVisibleLogin(tipo_usuario === null);
        fetchAxiosNoToken({
            url: `/pedidos_habitaciones/getOne/${id_reserva}/${id || 0}`,
        }).then((data) => {
            setPedido(data);

            if (data?.Cliente?.datos_facturacion_receptore) {
                const dfr = data.Cliente.datos_facturacion_receptore;
                setFactData({
                    tipo_comprobante: dfr.tipo_comprobante || "Factura",
                    correo: dfr.correo,
                    correo_copia: dfr.correo_copia || "",
                    direccion: dfr.direccion,
                    ubigeo: dfr.ubigeo,
                    nombre_comercial: dfr.nombre_comercial,
                    nombre_legal: dfr.nombre_legal,
                    tipo_documento_identidad:
                        dfr.tipo_documento_identidad as TipoDocSunat,
                    numero_documento_identidad: dfr.numero_documento_identidad,
                });
            }
        });
        getHouseFromTenant();
    }, [tipo_usuario, id_reserva, id]);

    const handleComprobante = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        setComprobante(e.target.files[0]);
        setComprobantePreview(URL.createObjectURL(e.target.files[0]));
    };

    const idCliete = () =>
        tipo_usuario === 8 ? 0 : pedido ? pedido.id_cliente : 0;

    const appendComprobanteToFormData = (fd: FormData) => {
        if (!isComprobante || !factData) {
            fd.append("is_igv", isComprobante ? "Si" : "No");
            return;
        }
        fd.append("tipo_comprobante", factData.tipo_comprobante);
        fd.append("is_igv", isComprobante ? "Si" : "No");
        fd.append("correo", factData.correo);
        if (factData.correo_copia)
            fd.append("correo_copia", factData.correo_copia);
        fd.append("direccion", factData.direccion);
        fd.append("ubigeo", factData.ubigeo);
        fd.append("nombre_comercial", factData.nombre_comercial);
        fd.append("nombre_legal", factData.nombre_legal);
        fd.append(
            "tipo_documento_identidad",
            factData.tipo_documento_identidad,
        );
        fd.append(
            "numero_documento_identidad",
            factData.numero_documento_identidad,
        );
    };

    const sendPayment = async () => {
        if (!comprobante) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Debes adjuntar el comprobante",
                icon: "warning",
            });
            return;
        }
        if (!pedido) return;

        if (mostrarOpcionComprobante && isComprobante && !factData) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Faltan datos del comprobante",
                text: "Completa los datos para emitir el comprobante.",
                icon: "warning",
            });
            return;
        }

        setEnviando(true);
        try {
            const form = new FormData();
            form.append("id_pedidos_habitaciones", `${id_reserva}`);
            form.append("id_cliente", `${idCliete()}`);
            if (
                factData &&
                factData.tipo_comprobante === "Factura" &&
                isComprobante &&
                !DOC_OPCIONES_FACTURA.find(
                    (o) => o.value === factData.tipo_documento_identidad,
                )
            ) {
                Swal.fire({
                    confirmButtonColor: "#029BFC",
                    iconColor: "#029BFC",
                    title: "Error en el tipo de documento",
                    text: "El tipo de documento seleccionado no es válido para emitir una factura.",
                    icon: "error",
                });
                return;
            }
            const monto =
                pedido.estado === "Pago Parcial"
                    ? pedido.saldo
                    : pagoUnico
                      ? String(isComprobante ? totalConIgv : baseTotal)
                      : String(
                            Math.ceil(
                                (Number(pedido.costo_total) * montoAPagar) /
                                    100,
                            ),
                        );

            form.append("monto", `${monto}`);
            form.append(
                "etapa_pago",
                `${pagoUnico ? "Pago Completo" : "Pago Parcial"}`,
            );
            form.append("pago_pedido", comprobante);

            appendComprobanteToFormData(form);

            await fetchAxiosNoToken({
                url: `/pedidos_pagos_habitaciones`,
                method: "post",
                body: form,
            });

            navigate("/comprobante-en-revicion");
        } catch (e: any) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: "No se pudo registrar el pago.",
                icon: "error",
            });
        } finally {
            setEnviando(false);
        }
    };

    const montoCalculo = useMemo(() => {
        if (!pedido) return 0;
        if (pedido.estado === "Pago Parcial") return Number(pedido.saldo);
        if (pagoUnico)
            return isComprobante ? totalConIgv : Number(pedido.costo_total);
        return Math.ceil((Number(pedido.costo_total) * montoAPagar) / 100);
    }, [pedido, pagoUnico, isComprobante, totalConIgv, montoAPagar]);

    useEffect(() => {
        if (paymentFailed === "true") {
            Swal.fire({
                title: "Pago no procesado",
                text: "Tu pago electrónico no se completó. Por favor intenta nuevamente.",
                icon: "error",
                confirmButtonColor: "#1C4CBB",
            });

            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
        }
    }, [paymentFailed]);

    const handleChangePagoUnico = (value: boolean) => {
        setPagoUnico(value);
        regenerateNiubizButton();
    };

    if (!pedido)
        return (
            <>
                <div className="fixed top-0 left-0 right-0 flex items-center justify-between p-3 font-medium text-center border shadow-md px-14 md:px-5">
                    <button
                        onClick={() => navigate("/")}
                        className="hover:underline"
                    >
                        Regresar al home
                    </button>
                    <NavbarsAvatar />
                </div>
                <div className="p-4 mt-20 font-medium">Cargando pedido...</div>
            </>
        );

    return (
        <div>
            {modalVisibleLogin && (
                <ModalLogin
                    modalVisibleLogin={modalVisibleLogin}
                    setModalVisibleLogin={setModalVisibleLogin}
                />
            )}
            {
                <ModalDatosComprobante
                    visible={showModalComprobante}
                    onClose={() => {
                        setShowModalComprobante(false);
                        if (!factData) setIsComprobante(false);
                    }}
                    initial={factData ?? undefined}
                    onSave={(data: any) => {
                        setFactData(data);
                        setIsComprobante(true);
                        setShowModalComprobante(false);
                        regenerateNiubizButton();
                    }}
                />
            }
            <div className="flex items-center justify-between p-3 font-medium text-center border shadow-md px-14 md:px-5">
                <button
                    onClick={() => navigate("/")}
                    className="hover:underline"
                >
                    Regresar al home
                </button>
                <NavbarsAvatar />
            </div>

            <div className="flex items-center justify-center p-3 font-medium text-center px-14 md:px-5">
                <p className="mt-4 text-2xl uppercase 2xl:text-4xl">
                    Pagar reserva
                </p>
            </div>

            <div className="grid w-full gap-10 p-4 mx-auto mt-3 2xl:mt-10 sm:grid-cols-2 max-w-7xl">
                {!showNiubiz ? (
                    <div className="w-full p-4 text-sm border-2 rounded-lg 2xl:text-base">
                        <input
                            type="radio"
                            checked={pagoUnico}
                            onChange={() => handleChangePagoUnico(true)}
                        />
                        <span className="ml-3 font-semibold">Pago único:</span>
                        <p>Realiza el pago total de tu reserva.</p>

                        <hr className="my-5 border-t-2 border-gray-300" />

                        {house.dos_pagos === "Si" && (
                            <div
                                className={
                                    pedido.estado === "Pago Parcial"
                                        ? "hidden"
                                        : ""
                                }
                            >
                                <input
                                    type="radio"
                                    checked={!pagoUnico}
                                    onChange={() =>
                                        handleChangePagoUnico(false)
                                    }
                                />
                                <span className="ml-3 font-semibold">
                                    Pago en dos partes:
                                </span>
                                <p>
                                    Realiza ahora el pago del {montoAPagar}% del
                                    alquiler que representa{" "}
                                    <span className="font-medium">
                                        {formatearNumero(
                                            Math.ceil(
                                                (Number(pedido.costo_total) *
                                                    montoAPagar) /
                                                    100,
                                            ),
                                        )}{" "}
                                        {pedido.tipo_moneda}
                                    </span>
                                </p>
                            </div>
                        )}

                        {mostrarOpcionComprobante && house.is_facturacion && (
                            <>
                                <hr className="my-5 border-t-2 border-gray-300" />
                                <label className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={isComprobante}
                                        onChange={(e) => {
                                            if (e.target.checked)
                                                setShowModalComprobante(true);
                                            else {
                                                setIsComprobante(false);
                                                setFactData(null);
                                            }
                                        }}
                                    />
                                    <span className="font-semibold">
                                        Solicitar comprobante (Factura/Boleta)
                                    </span>
                                </label>
                                {isComprobante && factData && (
                                    <p className="mt-1 text-xs text-gray-600">
                                        {factData.tipo_comprobante} · Doc:{" "}
                                        {factData.tipo_documento_identidad} ·{" "}
                                        {factData.numero_documento_identidad}{" "}
                                        {isIgv ? "· IGV 18%" : "· Sin IGV"}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col justify-between h-full text-sm text-gray-600">
                        <div className="flex flex-col gap-2">
                            <p className="mb-2 text-base font-semibold text-black">
                                Estás por pagar con tarjeta (Niubiz)
                            </p>

                            <p>
                                <span className="font-semibold">
                                    Tipo de pago:
                                </span>{" "}
                                {pagoUnico
                                    ? "Pago único"
                                    : `Pago en dos partes (${montoAPagar}%)`}
                            </p>

                            <p>
                                <span className="font-semibold">
                                    Monto a pagar:
                                </span>{" "}
                                {formatearNumero(montoCalculo)}{" "}
                                {pedido.tipo_moneda}
                            </p>

                            {isComprobante && factData ? (
                                <>
                                    <p className="mt-2 font-semibold">
                                        Comprobante solicitado:
                                    </p>
                                    <ul className="pl-4 list-disc">
                                        <li>
                                            Tipo: {factData.tipo_comprobante}
                                        </li>
                                        <li>
                                            Doc:{" "}
                                            {factData.tipo_documento_identidad}{" "}
                                            ·{" "}
                                            {
                                                factData.numero_documento_identidad
                                            }
                                        </li>
                                        <li>
                                            {isIgv
                                                ? "Con IGV (18%)"
                                                : "Sin IGV"}
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                <p className="mt-2 italic">
                                    Sin comprobante solicitado
                                </p>
                            )}
                        </div>

                        <button
                            type="button"
                            className="mt-4 text-sm text-blue-600 underline"
                            onClick={() => {
                                window.location.reload(); // fuerza recarga completa para evitar bugs
                            }}
                        >
                            Volver atrás o cambiar opciones de pago
                        </button>
                    </div>
                )}

                {/* Detalle e IGV */}
                <div className="flex flex-col w-full p-4 text-sm border-2 rounded-lg 2xl:text-base">
                    <div className="text-base ">
                        Tu pedido para{" "}
                        <span className="mx-1 font-medium">
                            {pedido.HotelHabitaciones?.nombre || "Tour"}.
                        </span>
                    </div>
                    <span>
                        Desde el{" "}
                        <span className="font-medium">
                            {pedido.fecha_inicio.split("-").reverse().join("-")}
                        </span>
                        , hasta el{" "}
                        <span className="font-medium">
                            {pedido.fecha_final.split("-").reverse().join("-")}
                        </span>
                        .
                    </span>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between mt-4">
                            <span className="font-medium">Precio alquiler</span>
                            <span className="font-medium">
                                {formatearNumero(Number(pedido.costo_alquiler))}{" "}
                                {pedido.tipo_moneda}
                            </span>
                        </div>

                        {Number(pedido.descuento_cupon) > 0 && (
                            <>
                                <div className="flex justify-between">
                                    <span className="text-green-500">
                                        Descuento por cupón
                                    </span>
                                    <span className="font-medium text-green-600">
                                        -{" "}
                                        {formatearNumero(
                                            Number(pedido.descuento_cupon),
                                        )}{" "}
                                        {pedido.tipo_moneda}
                                    </span>
                                </div>
                                <hr className="my-1" />
                            </>
                        )}

                        {Number(pedido.garantia) > 0 && (
                            <div className="flex justify-between mt-4">
                                <span className="font-medium">Garantía</span>
                                <span className="font-medium">
                                    {formatearNumero(Number(pedido.garantia))}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            </div>
                        )}
                        <hr className="my-1" />

                        {Number(pedido.descuento_estadia) > 0 && (
                            <>
                                <div className="flex justify-between">
                                    <span className="text-green-600">
                                        Descuento por larga estadía
                                    </span>
                                    <span className="font-medium text-green-600">
                                        -{" "}
                                        {formatearNumero(
                                            Number(pedido.descuento_estadia),
                                        )}{" "}
                                        {pedido.tipo_moneda}
                                    </span>
                                </div>
                                <hr className="my-1" />
                            </>
                        )}

                        {pedido.pedidos_servicios_adicionales.length > 0 && (
                            <>
                                <span className="font-medium">
                                    Servicios adicionales
                                </span>
                                {pedido.pedidos_servicios_adicionales.map(
                                    (s) => (
                                        <div
                                            key={s.id}
                                            className="flex justify-between"
                                        >
                                            <span>{s.servicio}</span>
                                            <span className="font-medium">
                                                {formatearNumero(
                                                    Number(s.costo),
                                                )}{" "}
                                                {pedido.tipo_moneda}
                                            </span>
                                        </div>
                                    ),
                                )}
                                <hr className="my-1" />
                            </>
                        )}

                        {pedido.pedidos_tours.length > 0 && (
                            <>
                                <span className="font-medium">Tours</span>
                                {pedido.pedidos_tours.map((s) => (
                                    <div
                                        key={s.id}
                                        className="flex justify-between"
                                    >
                                        <span>
                                            {s.hotel_tour.nombre} x
                                            {s.numero_adultos +
                                                s.numero_ninos}{" "}
                                        </span>
                                        <span className="font-medium">
                                            {formatearNumero(
                                                Number(s.precio_tour),
                                            )}{" "}
                                            {pedido.tipo_moneda}
                                        </span>
                                    </div>
                                ))}
                                <hr className="my-1" />
                            </>
                        )}
                    </div>

                    <div className="flex justify-between mt-4">
                        <span className="font-medium">
                            Precio total de la reserva
                        </span>
                        <span className="font-medium">
                            {formatearNumero(Number(pedido.costo_total))}{" "}
                            {pedido.tipo_moneda}
                        </span>
                    </div>

                    {isComprobante && (
                        <>
                            <div className="flex justify-between mt-2">
                                <span className="font-medium">
                                    IGV {isIgv ? "(18%)" : "(0%)"}
                                </span>
                                <span className="font-medium">
                                    {formatearNumero(igv)} {pedido.tipo_moneda}
                                </span>
                            </div>
                            <div className="flex justify-between mt-1">
                                <span className="font-semibold">
                                    Total con IGV
                                </span>
                                <span className="font-semibold">
                                    {formatearNumero(totalConIgv)}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Método de pago */}
                <div className="w-full p-3 border-2 rounded-lg 2xl:p-4">
                    {!showNiubiz && (
                        <select
                            onChange={(e) => {
                                setFormaPago(e.target.value);
                                regenerateNiubizButton();
                                if (e.target.value === "Pago electrónico") {
                                    setShowNiubiz(true);
                                } else {
                                    setShowNiubiz(false);
                                }
                            }}
                            value={formaPago}
                            className="bg-slate-100 2xl:mt-7 mt-3 2xl:p-3 p-2 max-w-[500px] w-full border-gray-200 border rounded-md disabled:cursor-not-allowed"
                        >
                            <option value="Transferencia bancaria">
                                Transferencia bancaria
                            </option>
                            {house.is_niubiz && (
                                <option value="Pago electrónico">
                                    Pago electrónico (tarjeta)
                                </option>
                            )}

                            {house.plan > 1 && mercadoPago === "Si" && (
                                <option value="mercado">
                                    Tarjeta de crédito o débito
                                </option>
                            )}
                        </select>
                    )}

                    {formaPago === "Transferencia bancaria" ? (
                        <div className="text-sm 2xl:text-base">
                            {/* ... tu bloque de instrucciones de transferencia y subida de imagen ... */}

                            <div className="relative flex mx-auto mt-4 bg-white border-4 border-dashed rounded-lg w-72 h-72 ">
                                {comprobantePreview ? (
                                    <img
                                        className="object-cover w-full h-full rounded-lg"
                                        src={comprobantePreview}
                                        alt="Comprobante de pago"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center p-3 ">
                                        <div className="flex flex-col items-center justify-center w-full mx-auto ">
                                            <div className="relative flex flex-col items-center justify-center">
                                                <span className="mt-2 font-bold text-center ">
                                                    Arrastre el comprobante
                                                </span>
                                                o
                                                <span className="font-normal text-center text-blue-500">
                                                    Sube la imagen desde tu
                                                    dispositivo
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    onChange={handleComprobante}
                                    accept="image/*"
                                    className="absolute top-0 left-0 opacity-0 cursor-pointer w-72 h-72 file:cursor-pointer"
                                />
                            </div>

                            <button
                                disabled={enviando}
                                onClick={sendPayment}
                                className="flex justify-center w-full max-w-xs p-2 mx-auto mt-4 border-2 rounded-lg hover:bg-slate-100 hover:scale-105 disabled:bg-slate-100"
                            >
                                {enviando ? "Enviando.." : "Enviar Comprobante"}
                            </button>
                        </div>
                    ) : formaPago === "Pago electrónico" ? (
                        // ==== BLOQUE NIUBIZ ====
                        <div className="flex flex-col items-start justify-start w-full mt-2">
                            <p className="mb-2 text-sm text-slate-600">
                                Paga con tarjeta de crédito/débito de forma
                                segura.
                            </p>

                            {pedido && (
                                <NiubizPayButton
                                    key={niubizRenderKey}
                                    renderKey={`niubiz-${niubizRenderKey}`}
                                    tipo_pedido="Habitación"
                                    idReserva={String(id_reserva)}
                                    tipoMoneda={pedido.tipo_moneda}
                                    amount={montoCalculo}
                                    tipoPago={
                                        pagoUnico
                                            ? "Pago Completo"
                                            : "Pago Parcial"
                                    }
                                    isComprobante={isComprobante}
                                    factData={factData}
                                    id_reserva={id_reserva}
                                    onReset={() => {
                                        window.location.reload();
                                    }}
                                />
                            )}

                            <small className="mt-2 text-xs text-slate-500">
                                Al confirmar, verás el modal seguro de Niubiz.
                                Al finalizar, te redireccionaremos
                                automáticamente según el resultado.
                            </small>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center mt-4">
                            {house?.plan > 1 && (
                                <p>
                                    Pago con tarjeta tiene un costo adicional
                                    del 5%.
                                </p>
                            )}
                            {house.is_mercado_pago === "Si" && llavePublica && (
                                <MercadoPago
                                    llavePublica={llavePublica}
                                    tipo_moneda={pedido.tipo_moneda}
                                    costo={
                                        pedido.estado === "Pago Parcial"
                                            ? Number(pedido.saldo)
                                            : pagoUnico
                                              ? isComprobante
                                                  ? totalConIgv
                                                  : Number(pedido.costo_total)
                                              : Number(pedido.costo_total) / 2
                                    }
                                    tipo_pago={
                                        pagoUnico
                                            ? "Pago Completo"
                                            : "Pago Parcial"
                                    }
                                    id_cliente={idCliete()}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col w-full p-4 text-sm border-2 rounded-lg h-60 2xl:text-base">
                    <div className="text-base font-semibold ">Resumen</div>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between mt-4">
                            <span className="font-medium">Pagado</span>
                            <span className="font-medium">
                                {formatearNumero(Number(pedido.pagado))}{" "}
                                {pedido.tipo_moneda}
                            </span>
                        </div>
                        <hr className="my-1" />
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between mt-4">
                            <span className="font-medium">Monto a pagar </span>
                            {pedido.estado === "Pago Parcial" ? (
                                <span className="font-medium">
                                    {formatearNumero(Number(pedido.saldo))}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            ) : pagoUnico ? (
                                <span className="font-medium">
                                    {formatearNumero(
                                        isComprobante ? totalConIgv : baseTotal,
                                    )}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            ) : (
                                <span className="font-medium">
                                    {montoCalculo
                                        ? formatearNumero(montoCalculo)
                                        : "0"}{" "}
                                    {pedido.tipo_moneda}
                                </span>
                            )}
                        </div>
                        <hr className="my-1" />
                    </div>

                    <div className="flex justify-between mt-4">
                        <span className="font-medium">Saldo pendiente</span>
                        {pagoUnico ? (
                            <span className="font-medium">
                                0 {pedido.tipo_moneda}
                            </span>
                        ) : (
                            <span className="font-medium">
                                {formatearNumero(
                                    Number(pedido.saldo) +
                                        (isComprobante ? igv : 0) -
                                        (montoCalculo ? montoCalculo : 0),
                                )}{" "}
                                {pedido.tipo_moneda}
                            </span>
                        )}
                    </div>

                    <div className="flex justify-between mt-4">
                        {pedido.garantia !== "0.00" &&
                            pedido.tipo_garantia === "Pago al ingreso casa" && (
                                <div className="flex w-full px-1 mt-4 bg-blue-200 border-4 border-blue-300 rounded-lg">
                                    <span className="underline">
                                        Garantía:{" "}
                                    </span>
                                    <span className="ml-2 italic font-thin ">
                                        Deberás pagar{" "}
                                        {formatearNumero(
                                            Number(pedido.garantia),
                                        )}{" "}
                                        {pedido.tipo_moneda} en efectivo al
                                        momento de llegar a la propiedad
                                    </span>
                                </div>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
};
